import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogOut, User, KeyRound, Pencil, Phone, MapPin, LayoutDashboard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { EditProfileDialog } from "@/components/EditProfileDialog";
import { PortalSections } from "@/components/PortalSections";

export default function MemberPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const loadProfile = () => api.get("/me/profile").then((r) => setProfile(r.data)).catch(() => {});

  useEffect(() => {
    loadProfile();
  }, []);

  const name = profile ? `${profile.first_name} ${profile.last_name}`.trim() : user?.name;

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-[#E5E0D8]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/whf-emblem.png" alt="WHF" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <p className="font-serif text-base font-semibold">World Harvest Fellowship</p>
              <p className="text-[10px] uppercase tracking-widest text-[#7A8A78]">Member Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setPwOpen(true)} data-testid="portal-change-password-button" className="h-10 border-[#E5E0D8] text-[#526050] hover:bg-[#F3EFEA]">
              <KeyRound className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">Change password</span>
            </Button>
            {user?.role !== "member" && (
              <Button variant="outline" onClick={() => navigate("/app")} data-testid="portal-dashboard-button" className="h-10 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
                <LayoutDashboard className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Dashboard</span>
              </Button>
            )}
            <Button variant="outline" onClick={logout} data-testid="portal-logout-button" className="h-10 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8 space-y-8" data-testid="member-portal">
        <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm flex items-center gap-4" data-testid="portal-profile">
          <div className="h-16 w-16 rounded-full overflow-hidden flex items-center justify-center text-white text-lg font-semibold shrink-0" style={{ backgroundColor: profile?.avatar_color || "#2D5A27" }}>
            {profile?.photo_path
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/members/${profile.id}/photo?v=${profile.photo_path}`} alt="Profile" className="h-full w-full object-cover" data-testid="portal-photo" />
              : (name ? name.split(" ").map((n) => n[0]).slice(0, 2).join("") : <User className="h-6 w-6" />)}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-serif text-2xl font-bold text-[#1C241B]">Welcome, {name || "Member"}</h1>
            <p className="text-sm text-[#7A8A78]">{user?.email}{profile?.status ? ` · ${profile.status}` : ""}</p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#526050]">
              {profile?.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-[#7A8A78]" />{profile.phone}</span>}
              {profile?.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#7A8A78]" />{profile.address}</span>}
            </div>
          </div>
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-profile-button" className="h-10 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8] shrink-0">
            <Pencil className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">Edit profile</span>
          </Button>
        </Card>

        <PortalSections />
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} profile={profile} onUpdated={loadProfile} />
    </div>
  );
}
